import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { styled } from '@mui/material/styles';
import { Button, Skeleton, Grid } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import GameMetaData from './GameMetaData';
import GameCard from './GameCard'; // Import the GameCard component
import { Link } from 'react-router-dom';
import CardActions from '@mui/material/CardActions';
import Card from '@mui/material/Card';

const PageWrapper = styled('main')(() => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  marginTop: '48px',
  padding: '16px 0',
}));

const DetailsWrapper = styled('div')(() => ({
  backgroundColor: 'white',
  width: '100%',
  borderRadius: '4px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

const ImageContainer = styled('div')(() => ({}));

const Image = styled('img')(() => ({
  width: '100%',
  maxHeight: '500px',
  objectFit: 'cover',
  borderRadius: '4px 4px 0 0',
}));

const DetailsContainer = styled('div')(() => ({
  padding: 'var(--spacing-5) 48px',
  '@media only screen and (max-width: 991px)': {
    padding: 'var(--spacing-5)',
  },
}));

const CardTitleContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const ViewsContainer = styled('span')(() => ({
  fontSize: '0.75rem',
  opacity: '0.5',
}));

const Title = styled('h1')(() => ({}));

const StyledMetaData = styled(GameMetaData)(() => ({
  padding: '16px 0 !important',
}));

const Description = styled('div')(() => ({}));

const ButtonsWrapper = styled(CardActions)(() => ({
  justifyContent: 'flex-end',
  padding: '16px',
}));

const CardButton = styled(Button)(() => ({
  color: 'white !important',
}));

const VisitSite = styled('span')(() => ({
  color: 'var(--primary-color) !important',
  lineHeight: '0',
}));

const NewTabIcon = styled(LaunchIcon)(() => ({
  marginLeft: '0.25rem !important',
  fontSize: '12px !important',
  color: 'var(--primary-color) !important',
}));

const SimilarGamesContainer = styled('div')(() => ({
  marginTop: '48px',
  // padding: '0 48px',
}));

const SkeletonLoader = () => (
  <DetailsWrapper>
    <Skeleton
      variant='rectangular'
      width='100%'
      height={500}
      style={{ marginBottom: '24px' }}
    />
    <DetailsContainer>
      <CardTitleContainer>
        <Skeleton variant='text' width='60%' height={40} />
        <Skeleton variant='text' width='20%' height={20} />
      </CardTitleContainer>
      {Array.from(new Array(5)).map((_, idx) => (
        <Skeleton
          key={idx}
          variant='text'
          width='100%'
          height={20}
          style={{ marginBottom: '16px' }}
        />
      ))}
      <Skeleton
        variant='rectangular'
        width='30%'
        height={40}
        style={{ marginTop: '32px' }}
      />
    </DetailsContainer>
  </DetailsWrapper>
);

export default function GameDetails() {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [similarGames, setSimilarGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchGame = async () => {
      try {
        const response = await fetch(
          `https://backend-apis.utilitips.com/wp-json/ut/v1/game/${id}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setGame(data);

        // Check if similar_to exists and set it
        if (data.similar_to) {
          setSimilarGames(data.similar_to);
        }

        document.title = `UtiliTips - ${data.title}`;
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGame();
  }, [id]);

  if (loading) {
    return (
      <PageWrapper>
        <SkeletonLoader />
      </PageWrapper>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const addBreaksToHtml = (html) => {
    return html.replace(/<\/p>/g, '</p><br />');
  };

  return (
    <PageWrapper>
      <Helmet>
        <title>{`UtiliTips - ${game.title}`}</title>
        <meta name='description' content={game.excerpt || 'A video game'} />
        <meta property='og:title' content={game.title} />
        <meta
          property='og:description'
          content={game.excerpt || 'A video game'}
        />
        <meta
          property='og:url'
          content={`https://www.utilitips.com/game/${id}`}
        />
        <meta
          property='og:image'
          content={game.featured_image || 'default-image-url.jpg'}
        />
        <link rel='canonical' href={`https://www.utilitips.com/game/${id}`} />
      </Helmet>

      {game ? (
        <>
          <DetailsWrapper>
            {game.featured_image ? (
              <ImageContainer>
                <Image src={game.featured_image} alt={game.title} />
              </ImageContainer>
            ) : null}

            <DetailsContainer>
              <CardTitleContainer>
                <Title>{game.title}</Title>
                {/* <ViewsContainer>
                  <i className='eye icon' />
                  {game.views}
                </ViewsContainer> */}
              </CardTitleContainer>

              <StyledMetaData
                category={game.category}
                company={game.company}
                platform={game.platform}
                loading={loading}
              />

              <Description
                dangerouslySetInnerHTML={{
                  __html: addBreaksToHtml(
                    game.content ? game.content : game.excerpt
                  ),
                }}
              />
            </DetailsContainer>
          </DetailsWrapper>

          {Array.isArray(similarGames) &&
            similarGames.length > 0 &&
            similarGames[0] !== false && (
              <SimilarGamesContainer>
                <h2>Similar Games</h2>
                <Grid container spacing={2}>
                  {similarGames.map((similarGame) => (
                    <Grid item xs={12} sm={6} md={4} key={similarGame.ID}>
                      <GameCard
                        image={similarGame.featured_image}
                        title={similarGame.post_title}
                        description={similarGame.post_content}
                        views={similarGame.views}
                        link={similarGame.guid}
                        slug={similarGame.ID}
                        type={similarGame.type}
                        categories={similarGame.categories}
                        company={similarGame.company}
                        platform={similarGame.platform}
                        externalButtonLabel='Visit Site'
                      />
                    </Grid>
                  ))}
                </Grid>
              </SimilarGamesContainer>
            )}
        </>
      ) : (
        <div>No similar games found!</div>
      )}
    </PageWrapper>
  );
}
